import { Crop } from 'react-image-crop'
import { ErrorLogger } from '@npco/utils-error-logger'

const CANVAS_AREA_LIMIT = 16777216

export const initialCrop: Crop = {
  height: 50,
  unit: '%',
  width: 50,
  x: 25,
  y: 25,
}

export const generateImagePreview = (
  canvas: HTMLCanvasElement | null,
  crop: Crop | null,
  onCrop: (image: string) => void
) => {
  if (!crop || !canvas) {
    return
  }

  try {
    const dataUrl = canvas.toDataURL('image/png')
    if (!dataUrl || dataUrl === 'data:,') {
      return
    }
    onCrop(dataUrl)
  } catch (error) {
    ErrorLogger.report('[Core] Cropping image', error)
  }
}

export const generateImagePreviewFile = async (
  canvas: HTMLCanvasElement | null,
  crop: Crop | null,
  onCropFile: (file: File) => void
) => {
  if (!crop || !canvas) {
    return
  }

  try {
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/png')
    })

    const file = new File([blob as Blob], 'image.png', {
      type: 'image/png',
    })

    onCropFile(file)
  } catch (error) {
    ErrorLogger.report('[Core] Cropping image', error)
  }
}

export const processFile = (
  completedCrop: Crop,
  previewCanvasRef: HTMLCanvasElement,
  imgRef: HTMLImageElement
) => {
  // NOTE: followed implementation here
  // https://codesandbox.io/s/react-image-crop-demo-with-react-hooks-y831o
  const ctx = previewCanvasRef.getContext('2d')

  if (!ctx || !completedCrop) {
    return
  }

  const scaleX = imgRef.naturalWidth / imgRef.width
  const scaleY = imgRef.naturalHeight / imgRef.height
  const pixelRatio = window.devicePixelRatio

  const initialWidth = Math.floor(completedCrop.width * scaleX * pixelRatio)
  const initialHeight = Math.floor(completedCrop.height * scaleY * pixelRatio)
  const exceedsLimit = initialWidth * initialHeight >= CANVAS_AREA_LIMIT

  if (exceedsLimit) {
    const ratio = Math.sqrt(CANVAS_AREA_LIMIT / (initialWidth * initialHeight))
    ctx.canvas.width = Math.floor(initialWidth * ratio)
    ctx.canvas.height = Math.floor(initialHeight * ratio)
  } else {
    ctx.canvas.width = initialWidth
    ctx.canvas.height = initialHeight
  }

  ctx.scale(pixelRatio, pixelRatio)
  ctx.imageSmoothingQuality = 'high'

  const cropX = completedCrop.x * scaleX
  const cropY = completedCrop.y * scaleY

  const centerX = imgRef.naturalWidth / 2
  const centerY = imgRef.naturalHeight / 2

  ctx.save()

  ctx.translate(-cropX, -cropY)
  ctx.translate(centerX, centerY)
  ctx.scale(1, 1)
  ctx.translate(-centerX, -centerY)
  ctx.drawImage(
    imgRef,
    0,
    0,
    imgRef.naturalWidth,
    imgRef.naturalHeight,
    0,
    0,
    imgRef.naturalWidth,
    imgRef.naturalHeight
  )

  ctx.restore()
}
